import { colors } from '@puzzlio/tokens';
import {
  MarkedCellType,
  type MarkedCell,
  type Tutorial,
  type TutorialContext,
  type TutorialStep,
  type TutorialSwatch,
  type TutorialTasks,
  type TutorialText,
  type CellState,
} from '@puzzlio/core';
import { FIELD1_CELL, FIELD2_CELL, cellValue, type CellValue, type FieldsBoard } from './types';

/*
  A step says what each cell *means* to it, and the grid decides how that looks. Nothing here names a
  class or a border: those differ between the web grid and the app's board.

  Returning no mark is how a cell says "nothing to draw here" — a cell whose requirement is already
  met is just an ordinary cell again.
*/
type MarkerFunction = (currentValue: CellValue) => [done: boolean, mark: MarkedCell | null];
type StepCell = [row: number, col: number, marker: MarkerFunction];

type Context = TutorialContext<FieldsBoard, CellState>;

/** Until the cell holds what the step asked for, it shows the colour that belongs there. */
const waitFor = (row: number, col: number, expected: CellValue): StepCell => [
  row,
  col,
  (current) =>
    current === expected
      ? [true, null]
      : [false, { row, col, markType: MarkedCellType.hintFill, markData: expected === FIELD1_CELL ? 'blue' : 'green' }],
];

const reference = (row: number, col: number): StepCell => [
  row,
  col,
  () => [true, { row, col, markType: MarkedCellType.reference }],
];

/** Every cell the step isn't about is dimmed, and out of play until the step moves on. */
const buildMarks = (cells: CellState[][], stepCells: StepCell[]): [done: boolean, marks: MarkedCell[]] => {
  const markers = new Map(stepCells.map(([row, col, marker]) => [`${row},${col}`, marker]));
  const marks: MarkedCell[] = [];
  let done = true;

  cells.forEach((rowCells, row) =>
    rowCells.forEach((cell, col) => {
      const marker = markers.get(`${row},${col}`);
      if (!marker) {
        marks.push({ row, col, markType: MarkedCellType.disabled });
        return;
      }
      const [cellDone, mark] = marker(cellValue(cell));
      if (mark) marks.push(mark);
      done &&= cellDone;
    })
  );

  return [done, marks];
};

const blue = (size: number): TutorialSwatch => ({ kind: 'swatch', fill: colors['blue-300'], label: `${size}` });
const green = (size: number): TutorialSwatch => ({
  kind: 'swatch',
  fill: colors['green-600'],
  ink: colors['white'],
  label: `${size}`,
});

/** A rules page: nothing to do on the board, which stays dimmed. */
const rulesStep =
  (heading: string | undefined, blocks: TutorialText[], showReset = true) =>
  ({ cells }: Context): TutorialStep => ({
    message: { kind: 'instruction', heading, blocks, done: true, showReset },
    marks: buildMarks(cells, [])[1],
  });

const tasks = (text: TutorialText, done: boolean): TutorialTasks => ({ kind: 'tasks', tasks: [{ text, done }] });

/** A move on the sample board: the step, and its one task, are done once every `waitFor` cell holds its colour. */
const boardStep =
  (stepCells: StepCell[], blocks: TutorialText[], task: TutorialText) =>
  ({ cells }: Context): TutorialStep => {
    const [done, marks] = buildMarks(cells, stepCells);
    return { message: { kind: 'instruction', blocks: [...blocks, tasks(task, done)], done }, marks };
  };

export const tutorial: Tutorial<FieldsBoard, CellState> = {
  game: 'fields',
  phases: [{ slug: 'tutorial', firstStep: 0 }],
  steps: [
    rulesStep(
      'Welcome to Fields!',
      ['Your goal is to color every cell blue or green to create fields of the correct sizes.'],
      false
    ),
    rulesStep('Rule 1', [
      'Each colored field has a single number that shows how many cells belong to that field.',
      ['Example: ', blue(3), ' means the field includes 3 connected blue cells.'],
    ]),
    rulesStep('Rule 2', [
      'Fields of the same color cannot touch along their edges. They may only touch diagonally.',
      "Let's try a sample board together!",
    ]),
    boardStep(
      [reference(1, 0), waitFor(2, 0, FIELD1_CELL), waitFor(1, 1, FIELD1_CELL)],
      [["Let's start with the ", green(1), " field. It has a size of one cell, so it's already complete!"]],
      ['Surround the ', green(1), ' with blue cells.']
    ),
    boardStep(
      [reference(0, 0), waitFor(0, 1, FIELD1_CELL)],
      [['The ', blue(3), ' needs to grow to be larger. It can only grow in one direction.']],
      ['Grow the ', blue(3), ' to the right.']
    ),
    boardStep(
      [
        reference(0, 0),
        reference(0, 1),
        reference(1, 1),
        waitFor(0, 2, FIELD2_CELL),
        waitFor(1, 2, FIELD2_CELL),
        waitFor(2, 1, FIELD2_CELL),
      ],
      [['Wow! We just completed the ', blue(3), ' field.']],
      ['Surround the ', blue(3), ' with green cells.']
    ),
    boardStep(
      [reference(2, 0), waitFor(3, 0, FIELD1_CELL)],
      ['This isolated blue cell needs to connect to a numbered field. It can only grow in one direction.'],
      'Grow the blue cell down.'
    ),
    boardStep(
      [reference(2, 0), reference(3, 0), reference(4, 0), waitFor(3, 1, FIELD2_CELL), waitFor(4, 1, FIELD2_CELL)],
      [['By doing that, we completed another ', blue(3), ' field!']],
      ['Surround the ', blue(3), ' with green cells.']
    ),
    boardStep(
      [
        reference(0, 3),
        reference(0, 2),
        reference(1, 2),
        reference(2, 1),
        reference(3, 1),
        reference(4, 1),
        waitFor(2, 2, FIELD1_CELL),
      ],
      [
        [
          "Let's step back and look at these two green fields. If this cell were green, it would make the ",
          green(5),
          ' field too big.',
        ],
      ],
      'Mark the cell blue.'
    ),
    boardStep(
      [reference(2, 4), reference(3, 3), waitFor(2, 3, FIELD2_CELL), waitFor(3, 4, FIELD2_CELL)],
      [
        [
          "Now, let's look at these two ",
          blue(3),
          ' fields. If either of these cells were blue, it would make the two fields touch.',
        ],
      ],
      'Mark both cells green.'
    ),
    boardStep(
      [reference(3, 3), waitFor(3, 2, FIELD1_CELL)],
      [['This ', blue(3), ' field needs to grow, and the only way to do that is to the left.']],
      ['Grow the ', blue(3), ' to the left.']
    ),
    boardStep(
      [reference(2, 2), reference(3, 2), reference(3, 3), waitFor(4, 2, FIELD2_CELL)],
      [['Yay! We completed the ', blue(3), ' field.']],
      ['Surround the ', blue(3), ' with green cells.']
    ),
    boardStep(
      [reference(2, 3), waitFor(1, 3, FIELD2_CELL)],
      ['This green cell must grow and connect to a numbered field. It can only grow up.'],
      'Grow the green cell up.'
    ),
    boardStep(
      [
        reference(0, 2),
        reference(0, 3),
        reference(1, 2),
        reference(1, 3),
        reference(2, 3),
        waitFor(0, 4, FIELD1_CELL),
        waitFor(1, 4, FIELD1_CELL),
      ],
      [
        ['That completed the ', green(5), ' field as well.'],
        ['Surrounding it with blue will complete the ', blue(3), ' field too!'],
      ],
      ['Surround the ', green(5), ' with blue cells.']
    ),
    ({ cells }) => {
      const [done, marks] = buildMarks(cells, [waitFor(4, 4, FIELD2_CELL)]);
      /*
        Solved, the board is unmarked: back to full strength, with nothing dimmed and nothing pointed
        at. It doesn't need locking either — the grid stops taking input on a complete board.
      */
      if (done) return { message: { kind: 'complete' } };
      return {
        message: {
          kind: 'instruction',
          blocks: [
            ['Finally, this last cell completes the ', green(7), ' field and the puzzle.'],
            tasks('Mark the last cell green.', done),
          ],
          done,
        },
        marks,
      };
    },
  ],
};
